<script setup lang="ts">
import BackstageDropdownMenu from '@/components/common/BackstageDropdownMenu.vue';
import { ButtonVariant } from '@/models/button-variant.enum';
import type { ActionDropdownItem } from '@/models/dropdown-item';
import { Icons } from '@/models/icons.enum';
import { useProductPricesStore } from '@/stores/product-prices';
import type { ProductMatrixGroupByOptions } from '@/types/matrix.interface';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BackstageButton from './BackstageButton.vue';

export interface NavBarItem {
  id: string;
  title: string;
  total: number;
  selected: boolean;
}
export interface Props {
  items: NavBarItem[];
  showQuantities: boolean;
  showWholesalePrice: boolean;
  showProductMatrixGroupByDropdown?: boolean;
  productMatrixGroupByOptions?: ProductMatrixGroupByOptions[];
}

const props = defineProps<Props>();

const { t } = useI18n();
const { shouldShowWholesalePriceOnProductMatrix } = storeToRefs(useProductPricesStore());
const isGroupByDropdownVisible = ref(false);
const dropdownItems = computed(() => {
  const dropdownItems: ActionDropdownItem[] = [];

  for (const option of props.productMatrixGroupByOptions || []) {
    dropdownItems.push({
      key: option,
      name: t(`matrix.group_by.${option}`),
      label: t(`matrix.group_by.${option}`),
      action: () => {
        selectGroupByOption(option);
      },
    });
  }

  return dropdownItems;
});

const emit = defineEmits<{
  selected: [value: string];
  'toggle-show-wholesale-price': [];
  'product-matrix-group-by': [option: ProductMatrixGroupByOptions];
}>();

let navBarItems = props.items;

watch(
  () => props.items,
  function () {
    navBarItems = props.items;
  },
);

function selectItem(item: NavBarItem) {
  navBarItems.map((navBarItem) => (navBarItem.selected = navBarItem.id === item.id));
  emit('selected', item.id);
}

function selectGroupByOption(option: ProductMatrixGroupByOptions) {
  emit('product-matrix-group-by', option);
  isGroupByDropdownVisible.value = false;
}

function toggleGroupByDropdown() {
  isGroupByDropdownVisible.value = !isGroupByDropdownVisible.value;
}

const handleClickOutside = () => {
  if (isGroupByDropdownVisible.value) {
    isGroupByDropdownVisible.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>
<template>
  <nav class="nav-bar">
    <ul class="nav-bar-nav">
      <li
        v-for="item in navBarItems"
        :key="item.id"
        :class="[{ selected: item.selected }]"
        class="nav-item"
        v-on:click="selectItem(item)">
        <div class="title">
          <p>
            {{ item.title }}
          </p>
          <span v-if="showQuantities">{{ '( ' + item.total + ' )' }}</span>
        </div>
      </li>
    </ul>

    <div class="nav-bar-actions">
      <div v-if="props.showProductMatrixGroupByDropdown" class="group-by-wrapper">
        <BackstageButton
          :variant="ButtonVariant.SECONDARY"
          :icon-right="Icons.CHEVRON_DOWN"
          data-cy="toggle-group-by-dropdown-button"
          @click.stop="toggleGroupByDropdown">
          {{ t('global.group_by') }}
        </BackstageButton>

        <BackstageDropdownMenu
          class="group-by-dropdown"
          :visible="isGroupByDropdownVisible"
          v-on:update:visible="isGroupByDropdownVisible = $event"
          v-on:item-selected="selectGroupByOption"
          :items="dropdownItems" />
      </div>

      <BackstageButton
        v-if="shouldShowWholesalePriceOnProductMatrix"
        v-on:click="emit('toggle-show-wholesale-price')"
        :variant="ButtonVariant.SECONDARY"
        id="toggle-show-wholesale-price-button"
        data-cy="toggle-show-wholesale-price-button"
        :icon-right="props.showWholesalePrice ? Icons.EYE_CLOSED : Icons.EYE_OPEN">
        {{ props.showWholesalePrice ? $t('matrix.hide_wholesale_price') : $t('matrix.show_wholesale_price') }}
      </BackstageButton>
    </div>
  </nav>
</template>

<style lang="scss" scoped>
.nav-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.nav-bar-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.group-by-wrapper {
  position: relative;
}

.group-by-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  width: 200px;
  height: 88.5px;
  gap: 0.5px;
  opacity: 1;
  border-bottom-width: 8px;
  background-color: var(--primary-bg-color);
  border-radius: 16px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 10;
}
</style>
